// The printed / downloaded prescription, built from a prescription sheet
// (GET /prescription-sheets/{appointmentId}). One template for every screen:
// doctor dashboard, clinic dashboard, patient profile and the patient's view.
import axios from "axios";

const esc = value =>
  String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const FREQ_SLOTS = ["Morning", "Afternoon", "Night", "Bedtime"];

/** "1-0-1" -> "Morning, Night"; "1/2-0-1" -> "½ Morning, Night"; anything else is shown as written. */
export function frequencyLabel(freq) {
  const value = String(freq || "").trim();
  if (!/^(\d+|1\/2|½)(-(\d+|1\/2|½)){2,3}$/.test(value)) return value;
  return value
    .split("-")
    .map((n, i) => (n === "0" ? null : `${n === "1" ? "" : `${n.replace("1/2", "½")} `}${FREQ_SLOTS[i]}`))
    .filter(Boolean)
    .join(", ");
}

export function durationLabel(duration) {
  const value = String(duration || "").trim();
  if (!value) return "";
  return /^\d+$/.test(value) ? `${value} day${value === "1" ? "" : "s"}` : value;
}

function dateLabel(value) {
  if (!value) return "";
  const d = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
  return isNaN(d) ? String(value) : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function genderLabel(g) {
  return { M: "Male", F: "Female", O: "Other" }[g] || "";
}

export async function loadSheet(appointmentId, { letterhead = true } = {}) {
  const { data } = await axios.get(`/prescription-sheets/${appointmentId}`, { params: { letterhead } });
  return data;
}

export function prescriptionHtml(sheet) {
  const { doctor, clinic, patient, appointment, prescription, medications, letterhead } = sheet;
  const rxDate = (prescription && prescription.date) || appointment.date;
  const note = prescription && prescription.note ? esc(prescription.note).replace(/\n/g, "<br>") : "";
  const ageSex = [patient.age ? `${esc(patient.age)} yrs` : "", genderLabel(patient.gender)].filter(Boolean).join(" / ") || "—";

  const header = letterhead && letterhead.header_image
    ? `<div class="rx-letterhead"><img src="${letterhead.header_image}" alt=""></div>`
    : `<div class="rx-head">
         <div>
           <div class="rx-doc">Dr. ${esc(doctor.name)}</div>
           <div class="rx-sub">${esc([doctor.qualifications, doctor.specialization].filter(Boolean).join(" · "))}</div>
           ${doctor.registration ? `<div class="rx-sub">Reg. No. ${esc(doctor.registration)}</div>` : ""}
         </div>
         <div class="rx-clinic">
           <div class="rx-clinic-name">${esc(clinic.name || "")}</div>
           ${clinic.address ? `<div>${esc(clinic.address)}${clinic.location ? `, ${esc(clinic.location)}` : ""}</div>` : ""}
           ${clinic.phone ? `<div>Ph: ${esc(clinic.phone)}</div>` : ""}
           ${clinic.email ? `<div>${esc(clinic.email)}</div>` : ""}
         </div>
       </div>`;

  const rows = medications.length
    ? medications.map((m, i) => `
        <tr>
          <td class="rx-n">${i + 1}</td>
          <td>
            <div class="rx-med">${esc(m.medicinename)}</div>
            <div class="rx-muted">${esc([m.brandname && m.brandname.toLowerCase() !== "n/a" ? m.brandname : "", m.dosage].filter(Boolean).join(" · "))}</div>
          </td>
          <td>${esc(frequencyLabel(m.frequency)) || "—"}</td>
          <td>${esc(durationLabel(m.duration)) || "—"}</td>
          <td>${esc(m.note) || "—"}</td>
        </tr>`).join("")
    : `<tr><td colspan="5" class="rx-muted rx-empty">No medicines prescribed.</td></tr>`;

  const signature = `
    <div class="rx-sign">
      ${letterhead && letterhead.signature ? `<img src="${letterhead.signature}" alt="">` : `<div class="rx-sign-space"></div>`}
      <div class="rx-sign-line"></div>
      <div class="rx-doc-sm">Dr. ${esc(doctor.name)}</div>
      ${doctor.registration ? `<div class="rx-muted">Reg. No. ${esc(doctor.registration)}</div>` : ""}
    </div>`;

  const footer = letterhead && letterhead.footer_image
    ? `<img class="rx-footer-img" src="${letterhead.footer_image}" alt="">`
    : `<div class="rx-footer-text">${esc([clinic.name, clinic.phone, clinic.email].filter(Boolean).join("  ·  "))}</div>`;

  return `
  <div class="rx-page">
    <style>
      .rx-page { width: 190mm; min-height: 270mm; margin: 0 auto; font-family: "Segoe UI", Arial, sans-serif; color: #1f2937; font-size: 12.5px; line-height: 1.45; display: flex; flex-direction: column; box-sizing: border-box; }
      .rx-page * { box-sizing: border-box; }
      .rx-letterhead img { width: 100%; max-height: 120px; object-fit: contain; display: block; }
      .rx-head { display: flex; justify-content: space-between; gap: 24px; }
      .rx-doc { font-size: 20px; font-weight: 700; color: #0b4f9c; }
      .rx-doc-sm { font-weight: 700; }
      .rx-sub, .rx-muted { color: #6b7280; font-size: 11.5px; }
      .rx-clinic { text-align: right; font-size: 11.5px; color: #4b5563; }
      .rx-clinic-name { font-size: 14px; font-weight: 700; color: #1f2937; }
      .rx-rule { height: 3px; background: #0e82fd; border-radius: 2px; margin: 12px 0 14px; }
      .rx-patient { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 6px 16px; background: #f3f6fa; border-radius: 8px; padding: 10px 14px; }
      .rx-label { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; }
      .rx-value { font-weight: 600; }
      .rx-section { margin-top: 14px; }
      .rx-section h4 { margin: 0 0 4px; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; }
      .rx-symbol { font-family: Georgia, serif; font-size: 30px; font-weight: 700; color: #0b4f9c; margin: 14px 0 4px; }
      table.rx-table { width: 100%; border-collapse: collapse; }
      .rx-table th { text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; color: #4b5563; border-bottom: 1.5px solid #d1d5db; padding: 6px 8px; }
      .rx-table td { padding: 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
      .rx-table tr { page-break-inside: avoid; }
      .rx-n { width: 22px; color: #6b7280; }
      .rx-med { font-weight: 700; }
      .rx-empty { text-align: center; padding: 18px; }
      .rx-bottom { margin-top: auto; padding-top: 24px; }
      .rx-sign { width: 220px; margin-left: auto; text-align: center; page-break-inside: avoid; }
      .rx-sign img { max-width: 180px; max-height: 70px; }
      .rx-sign-space { height: 50px; }
      .rx-sign-line { border-top: 1px solid #9ca3af; margin: 4px 0; }
      .rx-footer-img { width: 100%; max-height: 60px; object-fit: contain; margin-top: 14px; display: block; }
      .rx-footer-text { margin-top: 14px; border-top: 1px solid #e5e7eb; padding-top: 6px; text-align: center; font-size: 10.5px; color: #6b7280; }
      .rx-powered { text-align: center; font-size: 9.5px; color: #9ca3af; margin-top: 4px; }
    </style>
    ${header}
    <div class="rx-rule"></div>
    <div class="rx-patient">
      <div><div class="rx-label">Patient</div><div class="rx-value">${esc(patient.name)}</div></div>
      <div><div class="rx-label">Age / Sex</div><div class="rx-value">${ageSex}</div></div>
      <div><div class="rx-label">Patient ID</div><div class="rx-value">${esc(patient.id)}</div></div>
      <div><div class="rx-label">Date</div><div class="rx-value">${esc(dateLabel(rxDate))}</div></div>
    </div>
    ${appointment.reason ? `<div class="rx-section"><h4>Chief complaint</h4><div>${esc(appointment.reason)}</div></div>` : ""}
    ${note ? `<div class="rx-section"><h4>Diagnosis &amp; notes</h4><div>${note}</div></div>` : ""}
    <div class="rx-symbol">℞</div>
    <table class="rx-table">
      <thead><tr><th></th><th>Medicine</th><th>When</th><th>Duration</th><th>Instructions</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="rx-bottom">
      ${signature}
      ${footer}
      <div class="rx-powered">Appointment #${esc(appointment.id)} · Generated with YourDoc</div>
    </div>
  </div>`;
}

function fileName(sheet) {
  const name = String(sheet.patient.name || "patient").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
  return `prescription-${name}-${String((sheet.prescription && sheet.prescription.date) || sheet.appointment.date).slice(0, 10)}.pdf`;
}

export async function downloadPrescription(sheet) {
  const { default: html2pdf } = await import("html2pdf.js");
  const holder = document.createElement("div");
  holder.innerHTML = prescriptionHtml(sheet);
  await html2pdf()
    .set({
      margin: [10, 10, 10, 10],
      filename: fileName(sheet),
      image: { type: "jpeg", quality: 0.96 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    })
    .from(holder.firstElementChild)
    .save();
}

export function printPrescription(sheet) {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return false; // pop-up blocked: the caller can fall back to download
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Prescription - ${esc(sheet.patient.name)}</title>
    <style>@page { size: A4; margin: 10mm; } body { margin: 0; }</style></head>
    <body>${prescriptionHtml(sheet)}</body></html>`);
  win.document.close();
  // Images are inline data URLs, so a short delay is enough for layout.
  setTimeout(() => { win.focus(); win.print(); }, 350);
  return true;
}
